import { Button } from "@/components/ui/baseUi/button"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { useCurrentCoachIfExists } from "@/modules/coach/hooks/useProfessionalCoach"
import { cn } from "@/modules/lib/utils"
import { HUImageForm } from "@/modules/ui/components/huImageForm"
import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { ProgramInfoForm } from "@modules/programs/components/programInfoForm"
import { ProgramOnOffCopy } from "@modules/programs/components/programOnOffCopy"
import { UserProgramDetailsEntity } from "@modules/programs/entities/userProgramDetailsEntity"
import { UserProgramDraftEntity } from "@modules/programs/entities/userProgramDraftEntity"
import { useProgramCreate, useProgramDelete, useProgramUpdate } from "@modules/programs/hooks/usePrograms"
import { getProgramValidationSchema } from "@modules/programs/types/programFormikType"
import { getProgOnOffValidationSchema } from "@modules/programs/types/programOnOffFormikType"
import { ProgramStatus, ProgramType } from "@modules/programs/types/programTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { useFormik } from "formik"
import { LoaderCircle } from "lucide-react"
import { useEffect, useImperativeHandle, useRef } from "react"

type ProgramFormProps = {
  programType: ProgramType
  program?: UserProgramDetailsEntity
  ref: React.Ref<ProgramFormRef>
  className?: string
}

export type ProgramFormRef = {
  onClose: () => Promise<boolean>
}

export function ProgramForm({ program, programType, ref, className }: ProgramFormProps) {
  const mySpaceNavigate = useMySpaceNavigate()
  const confirmPopup = useConfirmPopup()
  const { hideSidebar } = useSidebar()
  const containerRef = useRef<HTMLFormElement>(null)
  const { deleteProgram, isDeletingProgram } = useProgramDelete()
  const programDraft = UserProgramDraftEntity.createDraft(programType, program)
  const { updateProgram, isUpdatingProgram } = useProgramUpdate()
  const { currentCoach: professionalUser } = useCurrentCoachIfExists()
  const { createProgram, isCreatingProgram } = useProgramCreate()

  useEffect(() => {
    document.querySelector('[data-slot="right-sidebar-content"]')?.scrollTo({ top: 0 })
  }, [])

  const confirmDeleteProgram = (programId: string, programName: string) => {
    confirmPopup.show({
      title: word("global.delete"),
      message: word("program.form.delete.confirmation.subtitle", {
        programName,
      }),
      accept: () => {
        deleteProgram({
          programId,
          onDeleted: () => {
            hideSidebar(false)
            mySpaceNavigate(Path.MySpace.Training)
          },
        })
          .then(() => {
            toast.success(word("program.delete.confirm.message"), { classNames: successToastClassName() })
          })
          .catch((error: unknown) => {
            const message = error instanceof Error ? error.message : String(error)
            toast.error(message, { classNames: errorToastClassName() })
          })
      },
      handleErrorMessage: (e: { code?: string; message?: string }) =>
        e.code === "OFFER_LINKED_TO_PROGRAM" ? word("programmation.delete.error.linked_to_offer") : (e.message ?? ""),
    })
  }

  const getValidationSchema = () => {
    if (programDraft.type === ProgramType.VIRTUAL || programDraft.type === ProgramType.PRO) {
      return getProgramValidationSchema({ imageRequired: !!program })
    } else if (programDraft.type === ProgramType.ONOFF) {
      return getProgOnOffValidationSchema({ imageRequired: !!program })
    }
  }

  const onSubmitSuccess = () => {
    toast.success(!program ? word("program.createProgram.success") : word("program.editProgram.success"), {
      classNames: successToastClassName(),
    })
    hideSidebar(false)
  }

  const onSubmitError = () => {
    toast.error(!program ? word("program.createProgram.error") : word("program.editProgram.error"), {
      classNames: errorToastClassName(),
    })
  }

  const formik = useFormik<UserProgramDraftEntity>({
    initialValues: programDraft,
    validationSchema: getValidationSchema(),
    onSubmit: () => {
      try {
        if (program) {
          if (
            program.type === ProgramType.ONOFF &&
            "duration" in formik.initialValues &&
            "duration" in formik.values &&
            formik.initialValues.duration &&
            formik.values.duration &&
            formik.initialValues.duration > formik.values.duration
          ) {
            confirmPopup.show({
              title: word("program.onOff.durationReductionWarning.title"),
              message: word("program.onOff.durationReductionWarning.message", {
                programName: program.name,
              }),
              accept: () => {
                return Promise.resolve(
                  void (() => {
                    void updateProgram({
                      programId: program.id,
                      programInput: UserProgramDraftEntity.formikToUpdateUserProgramInputDto(formik.values),
                    }).then(() => {
                      onSubmitSuccess()
                    })
                  }),
                )
              },
            })
          } else {
            void updateProgram({
              programId: program.id,
              programInput: UserProgramDraftEntity.formikToUpdateUserProgramInputDto(formik.values),
            }).then(() => {
              onSubmitSuccess()
            })
          }
        } else {
          void createProgram(UserProgramDraftEntity.formikToCreateUserProgramInputDto(formik.values)).then(
            (newProgram) => {
              if (!professionalUser) {
                if (newProgram.type === ProgramType.VIRTUAL || newProgram.type === ProgramType.PRO) {
                  mySpaceNavigate(Path.MySpace.ProgramDetails(newProgram.id))
                } else if (newProgram.type === ProgramType.ONOFF) {
                  mySpaceNavigate(Path.MySpace.ProgramOnOffDetails(newProgram.id))
                }
              }
              onSubmitSuccess()
            },
          )
        }
      } catch {
        onSubmitError()
      }
    },
  })

  useImperativeHandle(ref, () => ({
    onClose: async () => {
      if (Object.keys(formik.touched).length > 0) {
        const confirm = new Promise<boolean>((resolve) => {
          confirmPopup.show({
            title: word("program.form.closeMessage.title"),
            message: word("program.form.closeMessage.message"),
            accept: () => {
              resolve(true)
            },
            cancel: () => {
              resolve(false)
            },
            footerProps: {
              confirmText: word("global.ok"),
            },
          })
        })

        return confirm
      }
      return Promise.resolve(true)
    },
  }))

  const onValidateForm = async () => {
    await Promise.all(
      Object.keys(formik.values).map(async (inputKey) => {
        await formik.setFieldTouched(inputKey)
      }),
    )

    formik
      .validateForm()
      .then(async (value) => {
        if (Object.values(value).length === 0) {
          await formik.submitForm()
        } else {
          const errorElements = containerRef.current?.querySelectorAll('[aria-invalid="true"]')
          if (errorElements && errorElements.length > 0) {
            document
              .querySelector('[data-slot="right-sidebar-content"]')
              ?.scrollTo({ top: (errorElements[0] as HTMLElement).offsetTop - 30, behavior: "smooth" })
          }
        }
      })
      .catch((e: unknown) => {
        const message = e instanceof Error ? e.message : String(e)
        toast.error(message, { classNames: errorToastClassName() })
      })
  }

  const generateTitle = () => {
    if (program) {
      if (program.type === ProgramType.VIRTUAL) {
        return word("program.form.editProgram.title")
      } else if (program.type === ProgramType.ONOFF) {
        return word("program.onOff.formik.title.editProgram")
      } else if (program.type === ProgramType.PRO) {
        return word("program.pro.formik.title.editProgram")
      }
    } else if (programType === ProgramType.VIRTUAL) {
      return word("program.form.addProgram.title")
    } else if (programType === ProgramType.ONOFF) {
      return word("program.onOff.formik.title.createProgram")
    } else if (programType === ProgramType.PRO) {
      return word("program.pro.formik.title.createProgram")
    } else return "Error condition"
  }

  return (
    <div className={cn("flex flex-col gap-3 px-5 pt-20 pb-5", className)}>
      {/* HEADER */}
      <div className="flex flex-col">
        <Text fontStyle="HS">{generateTitle()}</Text>
        {!program && (
          <Text fontStyle="BS" className="mb-3">
            {word("program.form.addProgram.description")}
          </Text>
        )}
      </div>

      {/* WARNING */}
      <div className="flex flex-row items-start gap-2">
        <WarningHexagonalIcon color="var(--neutral-600)" width={24} height={24} />
        <Text fontStyle="BS" className="mb-3">
          {word("picture.warning.message")}
        </Text>
      </div>

      <form onSubmit={formik.handleSubmit} className="flex flex-col" ref={containerRef}>
        {programType === ProgramType.ONOFF && !program && "programIdToDuplicate" in formik.values && (
          <ProgramOnOffCopy formik={formik} />
        )}

        <InputContainer
          error={getFormErrorMessage("image", formik)}
          label={word("program.form.image.label")}
          isRequiredInput
        >
          <HUImageForm
            type="cover"
            setImage={(e) => {
              void formik.setFieldValue("image", e)
            }}
            image={formik.values.image}
            placeHolder={{
              message: word("program.form.uploadImage.text1"),
            }}
          />
        </InputContainer>

        <div className="flex flex-col justify-center">
          <ProgramInfoForm
            program={programDraft}
            isEditForm={program !== undefined}
            getFormErrorMessage={(input, errorMessage) => getFormErrorMessage(input, formik, { errorMessage })}
            formik={formik}
          />
          <div className="mt-5 mb-5 flex justify-end">
            <div className="flex grow flex-row gap-3">
              {program &&
              (program.type === ProgramType.VIRTUAL ||
                program.type === ProgramType.PRO ||
                (program.type === ProgramType.ONOFF && program.status !== ProgramStatus.PUBLISHED)) ? (
                <Button
                  variant="negativePrimary"
                  size="lg"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    confirmDeleteProgram(program.id, program.name)
                  }}
                  className="grow"
                  disabled={isDeletingProgram}
                >
                  {isDeletingProgram && <LoaderCircle className="animate-spin" />}
                  {word("global.delete")}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    hideSidebar()
                  }}
                  className="grow"
                >
                  {word("global.cancel")}
                </Button>
              )}

              <Button
                variant="primary"
                size="lg"
                onClick={() => void onValidateForm()}
                className="grow"
                disabled={isUpdatingProgram || isCreatingProgram}
                isLoading={isUpdatingProgram || isCreatingProgram}
              >
                {isUpdatingProgram || (isCreatingProgram && <LoaderCircle className="animate-spin" />)}
                {word("box.slot.form.submit.button")}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
